/**
 * Refreshing a cross-room feed from the live stream.
 *
 * The socket already keeps every room's counters current; the Overview and
 * Activity pages also hold a page the server assembled across rooms, and only
 * the server can rebuild that. Asking for it again on every live event costs
 * one feed request per event per open tab, which on a busy Space is a request
 * storm that says nothing new. So the ask is coalesced: a burst of arrivals is
 * one refetch, a few seconds after the first, and only when something landed
 * in a room the feed is actually showing.
 */

import { useEffect, useRef } from 'react'

import { subscribeToEvents } from '@/api/store'
import type { DurableEvent } from '@/api/types'

/** How long a feed waits after the first message event before asking again. */
export const LIVE_REFETCH_DELAY_MS = 5_000

/** The events that change what a message feed shows. */
const FEED_EVENTS: ReadonlySet<DurableEvent['type']> = new Set<DurableEvent['type']>([
  'message.created',
  'message.updated',
  'message.deleted',
  'attachment.created',
  'attachment.ready',
  'attachment.deleted',
])

export function useLiveRefetch(refetch: () => unknown, roomIds: readonly string[]): void {
  // Read inside the subscription without re-subscribing on every render: the
  // room list and the refetch function both arrive fresh each time.
  const refetchRef = useRef(refetch)
  refetchRef.current = refetch
  const roomsRef = useRef<ReadonlySet<string>>(new Set(roomIds))
  roomsRef.current = new Set(roomIds)

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null

    const unsubscribe = subscribeToEvents((event) => {
      if (!FEED_EVENTS.has(event.type)) return
      if (!roomsRef.current.has(event.room_id)) return
      if (timer !== null) return

      timer = setTimeout(() => {
        timer = null
        void refetchRef.current()
      }, LIVE_REFETCH_DELAY_MS)
    })

    return () => {
      unsubscribe()
      if (timer !== null) clearTimeout(timer)
    }
  }, [])
}
